import React, { useState, useEffect, Children, useRef, ReactNode, useCallback } from "react";
import {
  Col,
  Row,
  Menu,
  MenuProps,
  Avatar,
  Divider,
  Tooltip,
  Button,
  Flex,
  Tabs,
  Segmented,
  List,
  Skeleton,
  TreeSelect,
  Layout,
  Typography,
  Space,
  Modal,
} from "antd";
import {
  AppstoreOutlined,
  BellOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  ContactsOutlined,
  FilterOutlined,
  MailOutlined,
  MessageOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import ThreadItem from "./ThreadItem/ThreadItem";
import ScrollableLoadMore from "../ScrollableLoadMore";
import format from "../../../../utils/format";
import Moment from 'moment';
import { getMessageStatuses } from '../../../../reducers/message-status/resources';
import newMessageStatuses from '../../../inbox/new-message-statuses';
const { Header, Footer, Sider, Content } = Layout;
const { Text } = Typography;

// `onTranslateThread` (ADR-035): optional per-host on-demand translate action.
// The inbox passes nothing and ThreadItem falls back to the inbox-v2 store; the
// /contacts/:id page (own store) passes useContactProfileStore.translateThreadOnDemand.
const ThreadList = ({contactId,threads,retrieveThreads,contact,onTranslateThread}: any) => {
  const [loading, setLoading] = useState(false);
  const [page,setPage] = useState(1)
    const [eol,setEol] = useState(false)
    const [msgInfoDialog, setMsgInfoDialog] = useState(false);
    const [msgInfoData, setMsgInfoData] = useState<any>({});
    const [messageStatuses, setMessageStatuses] = useState<any[]>([]);

    const openMsgInfoDialog = (item) => {
      // Adapt from threads.js openMsgInfoDialog
      const {
        direction,
        message_status,
        provider_id,
        provider_code,
        updated_at,
        to,
        from,
        conversation,
        user,
        campaign,
      } = item;
      let message_desc = "";
      const provider = conversation?.botmap?.type || '';
      const isNexmo = provider === 'nexmo';

      // Similar logic as in threads.js for status details
      if(provider_code && provider === 'nexmo') {
        const filtered = messageStatuses.filter(status => status.provider_code === provider_code.toString());
        if(filtered.length) {
          message_desc = filtered[0].provider_description;
        }
      }

      // Similar logic as in threads.js getContact function
      const getContact = () => {
        // Message is from 1-1 texting
        if (user) return user.name

        // Message is from Campaign
        if (campaign) return `Broadcast - ${campaign.name}`

        // Sender is from ABT
        if(isNexmo && !conversation?.botmap?.default) return "Apply by Text"

        return "Virtual Agent"
      }

      // Similar logic as in threads.js
      const constructedData = {
        contact_name: contact?.name || '',
        gh_contact: getContact(),
        contact_num: to || '',
        sender_num: from || '',
        msg_timestamp: updated_at || item.created_at || '',
        message_status: ['webchat', 'messenger', 'twilio'].includes(provider) ? 'delivered' : message_status || "Unknown",
        provider,
        provider_id,
        provider_code,
        message_desc,
        direction
      };

      setMsgInfoData(constructedData);
      setMsgInfoDialog(true);
    };

    const closeMsgInfoDialog = () => {
      setMsgInfoDialog(false);
      setMsgInfoData({});
    };

    const MessageInfoBody = () => {
      const { contact_name, gh_contact, contact_num, sender_num, msg_timestamp, message_status, provider, message_desc, direction } = msgInfoData;

      const msgType = {
        inbound: "received",
        outbound: "sent",
        scheduled: "scheduled"
      };

      const providerList = {
        nexmo: "Nexmo",
        messagemedia: "MessageMedia",
        webchat: "Web Chat"
      };

      const outboundMsg = direction === 'outbound';

      return (
        <div className='msg-info-body'>
          <p><strong>To: </strong>{outboundMsg ? contact_name : gh_contact} - {format.forceFormat(outboundMsg ? contact_num : contact_num)}</p>
          <p><strong>From: </strong>{!outboundMsg ? contact_name : gh_contact} - {format.forceFormat(!outboundMsg ? sender_num : sender_num)}</p>
          <p><strong>Message {msgType[direction]}: </strong>{Moment(msg_timestamp).format('MMMM D, YYYY - hh:mm:ss.SSS A')}</p>
          {outboundMsg && message_status ? 
            <p>
              <strong>Message Status: </strong>
              <span style={{ textTransform: 'capitalize'}}>{message_status}</span>
            </p>
          : ''}
          {message_desc ? <p><strong>Status details: </strong>{message_desc}.</p> : ''}
        </div>
      );
    };
    

  const loadMoreData = () => {
    if (loading) return;
  
    setLoading(true);
    retrieveThreads(contactId,page)
    .then((eol) =>{
      setEol(eol)
      setPage(page+1) 
      setLoading(false)
    });
  };


   useEffect(()=>{
      console.log("changed contact:",contactId)
      setPage(1)
      setEol(false)
      // Load first page
      setLoading(true);
      retrieveThreads(contactId, 1)
      .then((eol) =>{
        setEol(eol)
        setPage(2) 
        setLoading(false)
      });
    },[contactId])
  
  
    // Removed the problematic useEffect that was causing infinite loading 

  useEffect(() => {
    // Fetch message statuses on component mount
    const fetchMessageStatuses = async () => {
      try {
        const response = await getMessageStatuses();
        const apiStatuses = response.data || [];
        const combinedStatuses = [...apiStatuses, ...newMessageStatuses.statuses];
        setMessageStatuses(combinedStatuses);
      } catch (error) {
        console.error('Failed to fetch message statuses:', error);
        // Fallback to just the static statuses
        setMessageStatuses(newMessageStatuses.statuses);
      }
    };

    fetchMessageStatuses();
  }, []); 
  
  const earliestTimestamp = threads.length > 0 ? Math.min(...threads.map(t => new Date(t.created_at).getTime())) : null;

  // Tapback reactions: reaction rows carry reaction={emoji, target_thread_id, removed?}
  // (parsed server-side from texts like `Loved "…"`). Aggregate emojis per target
  // bubble in chronological order so removals cancel earlier additions; reaction
  // rows whose target is on screen collapse into the badge (see ThreadItem).
  const reactionsByTarget = React.useMemo(() => {
    const map: Record<string, string[]> = {};
    const ordered = [...threads].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    for (const t of ordered) {
      const r = (t as any).reaction;
      if (!r || !r.target_thread_id || !r.emoji) continue;
      const list = map[r.target_thread_id] || (map[r.target_thread_id] = []);
      if (r.removed) {
        const idx = list.lastIndexOf(r.emoji);
        if (idx >= 0) list.splice(idx, 1);
      } else if (!list.includes(r.emoji)) {
        list.push(r.emoji);
      }
    }
    return map;
  }, [threads]);
  const threadIdSet = React.useMemo(() => new Set(threads.map(t => t.id)), [threads]);

  const threadListLoader = (
    <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
      {/* Inbound message skeleton - left aligned */}
      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
        <div style={{ maxWidth: '70%',display: 'flex', flexDirection: 'column' }}>
          <Skeleton.Button active style={{ width: 180, height: 40, borderRadius: 12 }} />
          <Skeleton.Input active size="small" style={{ width: 80, height: 12, marginTop: 4, display: 'block' }} />
        </div>
      </div>
      {/* Outbound message skeleton - right aligned */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <div style={{ maxWidth: '70%', textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
          <Skeleton.Button active style={{ width: 220, height: 50, borderRadius: 12 }} />
          <Skeleton.Input active size="small" style={{ width: 80, height: 12, marginTop: 4, marginLeft: 'auto', display: 'block' }} />
        </div>
      </div>
      {/* Inbound message skeleton */}
      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
        <div style={{ maxWidth: '70%', display: 'flex', flexDirection: 'column' }}>
          <Skeleton.Button active style={{ width: 150, height: 36, borderRadius: 12 }} />
          <Skeleton.Input active size="small" style={{ width: 80, height: 12, marginTop: 4, display: 'block' }} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <ScrollableLoadMore bgColor="#fafafa" items={threads} loadMoreData={loadMoreData} endOfListReached={eol} itemComponent={ThreadItem} inverse={true} endMessage="Start of Conversation" extraProps={{ openMsgInfoDialog, isFirst: (item) => earliestTimestamp !== null && new Date(item.created_at).getTime() === earliestTimestamp, reactionsByTarget, hasThread: (id) => threadIdSet.has(id), contactLanguage: contact?.language, translateThread: onTranslateThread }} loader={threadListLoader}/>
      <Modal
        title="Message Information"
        open={msgInfoDialog}
        onCancel={closeMsgInfoDialog}
        footer={null}
        style={{ zIndex: 10000 }}
      >
        <MessageInfoBody />
      </Modal>
    </>
  );
};
export default ThreadList;
